<script setup>
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import LanguageSwitcher from './LanguageSwitcher.vue'

const store = useStore()
const { t } = useI18n()

const loggedIn = computed(() => store.state.loggedIn)
const reports = computed(() => store.state.reports)
const themeMode = computed({
    get: () => store.state.themeMode,
    set: (value) => store.commit('setThemeMode', value)
})

const checkFcStatus = () => {
    if (typeof googlefc === "undefined") {
        alert(t('common.adBlockerWarning'))
        return
    }

    googlefc.callbackQueue.push(googlefc.showRevocationMessage)
}

const openDiscord = (e) => {
    if (e.target.href !== "#") {
        return
    }

    // If the link wasn't generated by the hover event, open the Discord invite link
    e.preventDefault()
    const inviteId = "dscKhHB9Jh".split("").reverse().join("")
    window.open(`https://discord.gg/${inviteId}`, "_blank")
}

const hoverDiscord = (e) => {
    const inviteId = "dscKhHB9Jh".split("").reverse().join("")
    e.target.href = `https://discord.gg/${inviteId}`
}

const openMail = (e) => {
    if (e.target.href !== "#") {
        return
    }

    e.preventDefault()
    // If the link wasn't generated by the hover event, open the mailto link
    const mail = "fatjlvohidcm@#ptrpovtwohnsmqaoizl!.ucaopm".split("").filter((_, i) => i % 2 === 0).join("")
    window.open(`mailto:${mail}?subject=KeyFrame%20Staff%20List`, "_blank")
}

const hoverMail = (e) => {
    const mail = "fatjlvohidcm@#ptrpovtwohnsmqaoizl!.ucaopm".split("").filter((_, i) => i % 2 === 0).join("")
    e.target.href = `mailto:${mail}?subject=KeyFrame%20Staff%20List`
}

const blurLink = (e) => {
    e.target.href = "#"
}
</script>

<template>
    <footer class="px-10 py-20 bg-ocean-400 text-white text-center">
        <div class="mx-auto max-w-[54em]">
            <div class="gap-x-6 gap-y-10 justify-between font-semibold lg:flex lg:flex-wrap lg:flex-row grid grid-cols-1 lg:divide-y-0 divide-y divide-white divide-opacity-10">
                <div class="flex flex-col gap-3 items-center lg:items-start pt-10 lg:pt-0">
                    <a href="/login.php" class="flex items-center gap-2 hover:underline" v-if="!loggedIn">
                        <font-awesome-icon icon="fa-right-to-bracket" />
                        {{ $t('nav.account.login') }}
                    </a>
                    <a href="/logout.php" class="flex items-center gap-2 hover:underline" v-if="loggedIn">
                        <font-awesome-icon icon="fa-right-from-bracket" />
                        {{ $t('nav.account.logout') }}
                    </a>
                    <router-link to="/account" class="flex items-center gap-2 hover:underline">
                        <font-awesome-icon icon="fa-user" />
                        {{ loggedIn ? $t('footer.navigation.myAccount') : $t('footer.navigation.preferences') }}
                    </router-link>
                    <label class="text-xs xl:text-sm flex items-center gap-2" for="footer-theme-selector">
                        <font-awesome-icon icon="fa-solid fa-circle-half-stroke" />
                        <span>
                            {{ $t('footer.navigation.theme') }}
                        </span>
                        <select
                            id="footer-theme-selector"
                            v-model="themeMode"
                            class="rounded-md border border-white border-opacity-35 bg-white px-2 py-1 text-gray-900 outline-none [color-scheme:light]"
                        >
                            <option value="light">{{ $t('footer.navigation.themeOptions.light') }}</option>
                            <option value="dark">{{ $t('footer.navigation.themeOptions.dark') }}</option>
                            <option value="system">{{ $t('footer.navigation.themeOptions.system') }}</option>
                        </select>
                    </label>
                    <router-link to="/history" class="flex items-center gap-2 hover:underline" v-if="loggedIn">
                        <font-awesome-icon icon="fa-clock-rotate-left" />
                        {{ $t('footer.navigation.myHistory') }}
                    </router-link>

                    <a href="javascript:googlefc.callbackQueue.push(googlefc.showRevocationMessage)" @click.prevent.stop="checkFcStatus" class="text-xs xl:text-sm flex items-center gap-2 hover:underline">
                        <font-awesome-icon icon="fa-cookie-bite" />
                        {{ $t('footer.navigation.privacyCookieSettings') }}
                    </a>
                    
                    <div class="mt-3">
                        <LanguageSwitcher />
                    </div>
                </div>
                <div class="flex flex-col gap-3 items-center lg:items-start pt-10 lg:pt-0">
                    <router-link to="/staff-lists" class="flex items-center gap-2 hover:underline">
                        <font-awesome-icon icon="fa-list" />
                        {{ $t('nav.stafflists.all') }}
                    </router-link>
                    <router-link to="/staff/create/1" class="flex items-center gap-2 hover:underline">
                        <font-awesome-icon icon="fa-add" />
                        {{ $t('footer.navigation.createStaffList') }}
                    </router-link>
                    <router-link to="/search" class="flex items-center gap-2 hover:underline">
                        <font-awesome-icon icon="fa-search" />
                        {{ $t('footer.navigation.advancedSearch') }}
                    </router-link>
                    <router-link to="/compare" class="flex items-center gap-2 hover:underline">
                        <font-awesome-icon icon="fa-code-compare" />
                        {{ $t('footer.navigation.compareStaffLists') }}
                    </router-link>
                    <router-link to="/roles" class="flex items-center gap-2 hover:underline">
                        <font-awesome-icon icon="fa-users" />
                        {{ $t('footer.navigation.roles') }}
                    </router-link>
                    <router-link to="/reports" class="flex items-center gap-2 hover:underline" v-if="loggedIn">
                        <font-awesome-icon icon="fa-flag" />
                        {{ $t('footer.navigation.reports') }}
                        <span class="flex items-center justify-center w-5 h-5 rounded-full bg-crimson-400 text-white text-xs float-right" v-if="reports > 0">
                            {{ reports }}
                        </span>
                    </router-link>
                </div>
                <div class="flex flex-col gap-3 items-center lg:items-start pt-10 lg:pt-0">
                    <router-link to="/tos" class="flex items-center gap-2 hover:underline">
                        <font-awesome-icon icon="fa-file-contract" />
                        {{ $t('footer.navigation.termsOfService') }}
                    </router-link>
                    <router-link to="/faq" class="flex items-center gap-2 hover:underline">
                        <font-awesome-icon icon="fa-question-circle" />
                        {{ $t('nav.help.faq') }}
                    </router-link>
                    <a href="#" target="_blank" class="flex items-center gap-2 hover:underline" @click="openMail" @mouseover="hoverMail" @mouseleave="blurLink" @focus="hoverMail" @focusout="blurLink">
                        <font-awesome-icon icon="fa-envelope" />
                        {{ $t('footer.social.contact') }}
                    </a>
                    <router-link to="/guidelines" class="flex items-center gap-2 hover:underline">
                        <font-awesome-icon icon="fa-book" />
                        {{ $t('footer.navigation.guidelines') }}
                    </router-link>
                    <router-link to="/stats" class="flex items-center gap-2 hover:underline">
                        <font-awesome-icon icon="fa-chart-simple" />
                        {{ $t('footer.navigation.stats') }}
                    </router-link>
                    <router-link to="/our-staff" class="flex items-center gap-2 hover:underline">
                        <font-awesome-icon icon="fa-users" />
                        {{ $t('footer.navigation.ourStaff') }}
                    </router-link>
                    <a href="/sitemap.xml" target="_blank" class="flex items-center gap-2 hover:underline">
                        <font-awesome-icon icon="fa-sitemap" />
                        {{ $t('footer.navigation.sitemap') }}
                    </a>
                    <router-link to="/scraping" class="flex items-center gap-2 hover:underline">
                        {{ $t('footer.navigation.scraping') }}
                    </router-link>
                </div>
                <div class="flex flex-col gap-3 items-center lg:items-start pt-10 lg:pt-0">
                    <a href="https://twitter.com/KeyFrameStaff" target="_blank" class="flex items-center gap-2 hover:underline">
                        <svg class="w-4 h-4" viewBox="0 0 248 204">
                            <path fill="#ffffff" d="M221.95 51.29c.15 2.17.15 4.34.15 6.53 0 66.73-50.8 143.69-143.69 143.69v-.04c-27.44.04-54.31-7.82-77.41-22.64 3.99.48 8 .72 12.02.73 22.74.02 44.83-7.61 62.72-21.66-21.61-.41-40.56-14.5-47.18-35.07 7.57 1.46 15.37 1.16 22.8-.87-23.56-4.76-40.51-25.46-40.51-49.5v-.64c7.02 3.91 14.88 6.08 22.92 6.32C11.58 63.31 4.74 33.79 18.14 10.71c25.64 31.55 63.47 50.73 104.08 52.76-4.07-17.54 1.49-35.92 14.61-48.25 20.34-19.12 52.33-18.14 71.45 2.19 11.31-2.23 22.15-6.38 32.07-12.26-3.77 11.69-11.66 21.62-22.2 27.93 10.01-1.18 19.79-3.86 29-7.95-6.78 10.16-15.32 19.01-25.2 26.16z"/>
                        </svg>

                        {{ $t('footer.social.twitter') }}
                    </a>
                    
                    <a href="https://bsky.app/profile/keyframe-staff-list.com" target="_blank" class="flex items-center gap-2 hover:underline">
                        <svg class="w-4" fill="none" viewBox="0 0 64 57">
                            <path fill="#ffffff" d="M13.873 3.805C21.21 9.332 29.103 20.537 32 26.55v15.882c0-.338-.13.044-.41.867-1.512 4.456-7.418 21.847-20.923 7.944-7.111-7.32-3.819-14.64 9.125-16.85-7.405 1.264-15.73-.825-18.014-9.015C1.12 23.022 0 8.51 0 6.55 0-3.268 8.579-.182 13.873 3.805ZM50.127 3.805C42.79 9.332 34.897 20.537 32 26.55v15.882c0-.338.13.044.41.867 1.512 4.456 7.418 21.847 20.923 7.944 7.111-7.32 3.819-14.64-9.125-16.85 7.405 1.264 15.73-.825 18.014-9.015C62.88 23.022 64 8.51 64 6.55c0-9.818-8.578-6.732-13.873-2.745Z"></path>
                        </svg>

                        {{ $t('footer.social.bluesky') }}
                    </a>
                    
                    <a href="#" target="_blank" class="flex items-center gap-2 hover:underline" @click="openDiscord" @mouseover="hoverDiscord" @mouseleave="blurLink" @focus="hoverDiscord" @focusout="blurLink">
                        <svg class="w-4 h-4" viewBox="0 0 127.14 96.36">
                            <path fill="#ffffff" d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z" />
                        </svg>

                        {{ $t('footer.social.discord') }}
                    </a>

                    <a href='https://ko-fi.com/G2G1196W5R' target='_blank'><img height='36' style='border:0px;height:36px;' src='https://storage.ko-fi.com/cdn/kofi5.png?v=6' border='0' :alt="$t('footer.social.buyMeACoffee')" /></a>
                    
                    <a href="https://keyframe-staff-list.com/silli/" target="_blank" class="flex items-center gap-2 hover:underline">
                        {{ $t('footer.social.silli') }}
                    </a>
                    <a href="https://keyframe-staff-list.com/romanizer/korean.html" target="_blank" class="flex items-center gap-2 hover:underline">
                        {{ $t('footer.navigation.koreanRomanizer') }}
                    </a>
                </div>
            </div>

            <hr class="my-12 border-white border-opacity-10" />

            <p class="text-justify">
                <i18n-t keypath="footer.disclaimer">
                    <template #animeDatabaseLink>
                        <a href="https://seesaawiki.jp/w/radioi_34/" target="_blank" class="underline" rel="noopener">アニメスタッフデータベース</a>
                    </template>
                    <template #anilistLink>
                        <a href="https://anilist.co/" target="_blank" class="underline" rel="noopener">AniList</a>
                    </template>
                </i18n-t>
            </p>
        </div>
    </footer>
</template>
